import { type CSSProperties } from 'react';

import { cn } from '@/shared/lib/utils';
import { formatIsoDate } from './dateCore';

/** Shared per-render context for every day cell (built once by CalendarWheels). */
export interface DayContext {
  mode: 'single' | 'range';
  from: string | null;
  to: string | null;
  hoverIso: string | null;
  hoverDir: 'fwd' | 'bwd';
  infHover: boolean;
  nights: boolean;
  viewMonth: number;
  today: string;
  cellW: number;
  cellH: number;
  isDisabled: (iso: string) => boolean;
  dayTint?: (iso: string) => string | null;
  noteFor: (iso: string) => { text: string; color?: string } | null;
  onSelectDay: (iso: string) => void;
  onHoverDay: (iso: string, dir: 'fwd' | 'bwd') => void;
}

/** One day cell with range / ∞-preview / nights / today / disabled decoration. */
export function CalendarDay({ date, ctx }: { date: Date; ctx: DayContext }) {
  const {
    mode,
    from,
    to,
    hoverIso,
    hoverDir,
    infHover,
    nights,
    viewMonth,
    today,
    cellW,
    cellH,
    isDisabled,
    dayTint,
    noteFor,
    onSelectDay,
    onHoverDay,
  } = ctx;

  const iso = formatIsoDate(date);
  const disabled = isDisabled(iso);
  const outside = date.getMonth() !== viewMonth;
  const weekday = date.getDay();
  const weekend = weekday === 0 || weekday === 6;
  const fromReal = !!from && from !== '-INF' && from !== 'INF';
  const toReal = !!to && to !== 'INF' && to !== '-INF';
  const selecting = mode === 'range' && fromReal && !to;

  let lo = from;
  let hi = to;
  let previewInf: 'fwd' | 'bwd' | null = null;
  if (selecting) {
    if (infHover) {
      previewInf = hoverDir || 'fwd';
    } else if (hoverIso) {
      if (hoverIso < (from as string)) {
        lo = hoverIso;
        hi = from;
      } else {
        lo = from;
        hi = hoverIso;
      }
    }
  }

  const isStart = fromReal && iso === from;
  const isEnd = toReal && iso === to;
  const nightsMode = mode === 'range' && nights;
  const loReal = !!lo && lo !== '-INF' && lo !== 'INF';
  const hiReal = !!hi && hi !== 'INF' && hi !== '-INF';
  const isLo = loReal && iso === lo;
  const isHi = hiReal && iso === hi;
  const nightsPair = nightsMode && loReal && hiReal && lo !== hi && !previewInf;
  const selected = (mode === 'single' && iso === from) || isStart || isEnd;

  let inRange = false;
  if (mode === 'range') {
    if (previewInf === 'fwd') inRange = iso > (from as string);
    else if (previewInf === 'bwd') inRange = iso < (from as string);
    else if (to === 'INF' && fromReal) inRange = iso > (from as string);
    else if (from === '-INF' && toReal) inRange = iso < (to as string);
    else if (loReal && hiReal) inRange = iso > (lo as string) && iso < (hi as string);
  }

  const isToday = iso === today;
  const note = noteFor(iso);
  const tint = dayTint?.(iso) ?? null;

  const style: CSSProperties = {
    position: 'relative',
    width: cellW,
    height: cellH,
    fontSize: note ? 13 : 12,
    color: disabled
      ? 'var(--phx-text-faint)'
      : outside
        ? 'var(--phx-text-faint)'
        : weekend
          ? 'var(--phx-text-muted)'
          : 'var(--phx-text)',
  };
  let borderRadius: number | undefined = 4;
  let hoverable = false;

  if (tint && !selected && !inRange && !disabled) style.background = tint;
  if (inRange) {
    style.background = 'var(--phx-primary-tint)';
    borderRadius = 0;
    style.color = 'var(--phx-text)';
  }
  if (iso === lo && hi && iso !== hi) {
    style.borderTopRightRadius = 0;
    style.borderBottomRightRadius = 0;
  }
  if (iso === hi && lo && iso !== lo) {
    style.borderTopLeftRadius = 0;
    style.borderBottomLeftRadius = 0;
  }
  if (nightsPair && (isLo || isHi)) {
    style.background = isLo
      ? 'linear-gradient(135deg, transparent 0 50%, var(--phx-primary) 50% 100%)'
      : 'linear-gradient(135deg, var(--phx-primary) 0 50%, transparent 50% 100%)';
    style.color = 'var(--phx-text)';
    style.fontWeight = 600;
    borderRadius = 0;
  } else if (selected) {
    style.background = 'var(--phx-primary)';
    style.color = 'var(--phx-primary-fg)';
    style.fontWeight = 600;
  }
  if (isToday && !selected) style.boxShadow = 'inset 0 0 0 1px var(--phx-primary)';
  if (disabled && !inRange) {
    style.textDecoration = 'line-through';
    style.opacity = 0.6;
  }
  if (borderRadius != null && style.borderTopRightRadius == null) style.borderRadius = borderRadius;
  if (!disabled && !selected && !inRange && !tint && !nightsPair) hoverable = true;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelectDay(iso)}
      onMouseEnter={() => {
        if (mode === 'range') {
          const dir: 'fwd' | 'bwd' = fromReal && iso < (from as string) ? 'bwd' : 'fwd';
          onHoverDay(iso, dir);
        }
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-0 border-0 p-0 leading-[1.05] tabular-nums',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        hoverable && 'hover:bg-phx-surface-2'
      )}
      style={style}
    >
      <span style={{ fontWeight: note ? 600 : 'inherit' }}>{date.getDate()}</span>
      {note ? (
        <span
          className="text-phx-3xs mt-0.5 leading-none font-semibold"
          style={{
            color: selected ? 'var(--phx-primary-fg)' : (note.color ?? 'var(--phx-text-muted)'),
          }}
        >
          {disabled ? '—' : note.text}
        </span>
      ) : null}
    </button>
  );
}
